import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { OPS_METRICS_TYPES } from "@amzn/asx-ui-telemetry-client";
import { SectionExpander, Text } from "@amzn/storm-ui";
import React, { useState } from "react";

import { LABELS } from "../../../constants/common";
import {
    CLOUDWATCH_RUM_COUNTERS,
    TELEMETRY_ID,
    PHOENIX_FEATURE,
} from "../../../constants/telemetry";
import {
    logWithAsxTelemetryClient,
    logWithTakt,
} from "../../../utils/telemetry.utils";

import {
    StyledErrorBoundaryCard,
    StyledErrorBoundaryContainer,
    StyledErrorBoundaryText,
} from "./ErrorBoundaryStyles";

interface ErrorBoundaryState {
    error: string;
    stack: string;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

export const ErrorDetails: React.FC<{ error: string; stack: string }> = ({
    error,
    stack,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <SectionExpander
            label="Error details"
            handleClick={() => setIsOpen(!isOpen)}
            isOpen={isOpen}
        >
            <Text
                style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "left",
                }}
            >
                {error}
            </Text>
            {stack && (
                <pre
                    style={{
                        fontSize: "10px",
                        color: "#666",
                        textAlign: "left",
                        overflow: "auto",
                        margin: "8px 0 0 0",
                        whiteSpace: "pre",
                    }}
                >
                    {stack}
                </pre>
            )}
        </SectionExpander>
    );
};

export class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { error: "", stack: "" };
    }

    static getDerivedStateFromError(error: Error): {
        error: string;
        stack: string;
    } {
        // Update state so the next render will show the fallback UI.
        return { error: error.message, stack: error.stack || "" };
    }

    componentDidCatch(error: Error): void {
        logWithTakt(TELEMETRY_ID.DISCOVERY_ERROR_PAGE, {
            errorMessage: error.message,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.DISCOVERY_ERROR_PAGE,
            PHOENIX_FEATURE.DISCOVERY,
            {
                customData: {
                    errorMessage: error.message,
                },
            },
        );
        logWithAsxTelemetryClient(
            CLOUDWATCH_RUM_COUNTERS.UNHANDLED_CLIENT_ERROR_BOUNDARY,
            OPS_METRICS_TYPES.Error,
            1,
            {
                errorMessage: error.message,
            },
        );
    }

    render(): JSX.Element | React.ReactNode {
        const { children } = this.props;
        const { error, stack } = this.state;

        if (error) {
            return (
                <StyledErrorBoundaryContainer justifyContent="center">
                    <StyledErrorBoundaryCard>
                        <StyledErrorBoundaryText renderAs="h2">
                            {LABELS.WHOOPS_SOMETHING_WENT_WRONG}
                        </StyledErrorBoundaryText>
                        <StyledErrorBoundaryText renderAs="h4">
                            {LABELS.REFRESH_OR_TRY_AGAIN_LATER}
                        </StyledErrorBoundaryText>
                        <ErrorDetails error={error} stack={stack} />
                    </StyledErrorBoundaryCard>
                </StyledErrorBoundaryContainer>
            );
        }

        return children;
    }
}
